/**
 * AP-7 — Signalauswertung fuer das Signal-Detailfenster.
 *
 * 1:1-Portierung von ExtractRawSignal(), DecodeSignalValue() und
 * FormatSignalValue() aus DbcParser.cpp. Bewusst hier und nicht ueber das
 * WASM-Modul: `cm_last_decode_json` liefert als `dbc.summary` nur den Text der
 * Description-Spalte (den CM_-Kommentar der Botschaft), das Detailfenster und
 * die aufgeklappte Signalliste brauchen aber jedes Signal einzeln. Die
 * Metadaten dafuer stehen ohnehin im `dbc`-Snapshot.
 */

/** Ein Signal aus dem `dbc`-Snapshot (CONTRACTS.md §5.3). */
export interface DbcSignalMeta {
  name: string;
  startBit: number;
  length: number;
  bigEndian: boolean;
  isSigned: boolean;
  scale: number;
  offset: number;
  unit: string;
  /** CM_ SG_ */
  comment: string;
  /** BA_ "SPN", 0 = keine. */
  spn: number;
  /** VAL_-Tabelle als [Rohwert, Text] in Dateireihenfolge. */
  values: Array<[number, string]>;
}

export interface DbcMessageMeta {
  frameId: number;
  extended: boolean;
  name: string;
  dlc: number;
  /** CM_ BO_ */
  comment: string;
  signals: DbcSignalMeta[];
}

export interface DbcSnapshot {
  messages: DbcMessageMeta[];
}

/**
 * BigInt statt number: ein Signal darf bis zu 64 Bit breit sein, und
 * JavaScripts bitweise Operatoren rechnen mit 32 Bit — ab 32 Bit Laenge
 * waere das Ergebnis sonst still falsch.
 */
function extractRawSignal(data: Uint8Array, dataLen: number, sig: DbcSignalMeta): bigint {
  let raw = 0n;
  if (sig.length <= 0 || sig.length > 64) return 0n;

  if (!sig.bigEndian) {
    // Intel / little endian: das Startbit ist das niederwertigste Bit.
    for (let i = 0; i < sig.length; i += 1) {
      const bitPos = sig.startBit + i;
      const byteIndex = bitPos >> 3;
      const bitIndexInByte = bitPos & 7;
      if (byteIndex < 0 || byteIndex >= dataLen) continue;
      if (((data[byteIndex] ?? 0) & (1 << bitIndexInByte)) !== 0) raw |= 1n << BigInt(i);
    }
    return raw;
  }

  // Motorola / big endian: die "Saegezahn"-Bitnummerierung der DBC.
  let pos = sig.startBit;
  for (let i = 0; i < sig.length; i += 1) {
    const byteIndex = pos >> 3;
    const bitIndexInByte = pos & 7;
    if (byteIndex >= 0 && byteIndex < dataLen && ((data[byteIndex] ?? 0) & (1 << bitIndexInByte)) !== 0) {
      raw |= 1n << BigInt(sig.length - 1 - i);
    }
    pos += bitIndexInByte === 0 ? 15 : -1;
  }
  return raw;
}

export function decodeSignalValue(sig: DbcSignalMeta, data: Uint8Array, dataLen: number): number {
  const raw = extractRawSignal(data, dataLen, sig);

  let rawValue: number;
  if (sig.isSigned && sig.length > 0 && sig.length < 64 && (raw & (1n << BigInt(sig.length - 1))) !== 0n) {
    rawValue = Number(raw - (1n << BigInt(sig.length)));
  } else {
    rawValue = Number(raw);
  }

  return rawValue * sig.scale + sig.offset;
}

/** "%.0f" fuer ganze Zahlen unter 1e9, sonst "%.3f"; NaN wird zu "-". */
export function formatSignalValue(value: number): string {
  if (Number.isNaN(value)) return '-';
  if (Math.floor(value) === value && Math.abs(value) < 1e9) return value.toFixed(0);
  return value.toFixed(3);
}

/** `DbcSignal::ValueText()` — VAL_ ist ueber den ROHWERT verschluesselt. */
export function signalValueText(sig: DbcSignalMeta, value: number): string | undefined {
  if (sig.values.length === 0 || sig.scale === 0 || Number.isNaN(value)) return undefined;
  const raw = (value - sig.offset) / sig.scale;
  if (Math.abs(raw - Math.round(raw)) > 1e-6) return undefined;
  const key = Math.round(raw);
  return sig.values.find(([raw2]) => raw2 === key)?.[1];
}

/** `FormatSignalDisplay()`: VAL_-Text, sonst Zahl mit Einheit. */
export function formatSignalDisplay(sig: DbcSignalMeta, value: number, includeUnit = true): string {
  const text = formatSignalValue(value);
  const label = signalValueText(sig, value);
  if (label !== undefined) return `${text} (${label})`;
  if (includeUnit && sig.unit !== '') return `${text} ${sig.unit}`;
  return text;
}

/** `MaxSpnDigits()`: breiteste SPN der Botschaft, damit die Zeilen fluchten. */
export function maxSpnDigits(message: DbcMessageMeta): number {
  let digits = 0;
  for (const sig of message.signals) {
    if (sig.spn > 0) digits = Math.max(digits, String(sig.spn).length);
  }
  return digits;
}

/**
 * Beschreibungstext eines Signals: SPN-Nummer und CM_ SG_-Kommentar. Die Nummer
 * wird auf `spnDigits` rechtsbuendig aufgefuellt (Signale ohne SPN bekommen
 * Leerzeichen), damit der Bindestrich in der dicktengleichen Tabelle in einer
 * Spalte steht — `FormatSignalInfo()`.
 */
export function signalInfoText(sig: DbcSignalMeta, spnDigits = 0): string {
  if (spnDigits <= 0) return sig.comment;
  const number = sig.spn > 0 ? String(sig.spn) : '';
  const info = (sig.spn > 0 ? 'SPN ' : '    ') + ' '.repeat(spnDigits - number.length) + number;
  return sig.comment === '' ? info : `${info} - ${sig.comment}`;
}

/** "%g" aus den Spalten Scale/Offset des DBC-Browsers. */
export function formatG(value: number): string {
  if (!Number.isFinite(value)) return String(value);
  if (value === 0) return '0';
  const exponent = Math.floor(Math.log10(Math.abs(value)));
  if (exponent < -5 || exponent >= 6) {
    return value.toExponential(5).replace(/\.?0+e/, 'e').replace('e+', 'e+').replace('e-', 'e-');
  }
  return String(Number(value.toPrecision(6)));
}

/** "%08X" fuer erweiterte, sonst "%03X" — wie PopulateMessages(). */
export function formatDbcId(frameId: number, extended: boolean): string {
  return (frameId >>> 0).toString(16).toUpperCase().padStart(extended ? 8 : 3, '0');
}

/**
 * `DbcDatabase::J1939PgnKey()`: Prioritaet (Bits 28:26) und Quelladresse
 * ausblenden, bei PDU1 (PF < 0xF0) auch die Zieladresse.
 */
export function j1939MessageKey(id29: number): number {
  const pf = (id29 >>> 16) & 0xff;
  return (id29 & (pf < 0xf0 ? 0x03ff0000 : 0x03ffff00)) >>> 0;
}

/**
 * `DbcDatabase::FindByFrameId()`: erst die exakte ID, dann der J1939-Treffer
 * ueber die PGN allein. Ohne diesen Fallback oeffnet der Doppelklick nur fuer
 * Botschaften, deren Quelladresse zufaellig der in der DBC entspricht (EEC1
 * steht dort als 0x0CF004FE, gesendet wird sie meist mit SA 0x00).
 */
export function findDbcMessage(
  messages: readonly DbcMessageMeta[] | undefined,
  frameId: number,
): DbcMessageMeta | undefined {
  if (messages === undefined) return undefined;
  const exact = messages.find((entry) => entry.frameId === frameId);
  if (exact !== undefined) return exact;
  if (frameId <= 0x7ff) return undefined;
  const key = j1939MessageKey(frameId);
  return messages.find((entry) => entry.extended && j1939MessageKey(entry.frameId) === key);
}

/**
 * Fensterschluessel je Botschaft: erweiterte IDs teilen sich das Fenster ueber
 * die PGN (wie `detailWindows_`), 11-Bit-IDs bleiben — anders als im Original,
 * wo `J1939PgnKey()` sie alle auf 0 abbildet — je ID getrennt.
 */
export function signalDetailKey(frameId: number): number {
  return frameId > 0x7ff ? j1939MessageKey(frameId) : frameId;
}
